import { StepTrackerStatus } from '@/components/navigation/StepTracker/StepTracker';

/**
 * Two levels of structure in this flow, and they are not the same thing:
 *
 * - A **step** is a position in the header's step tracker. Prev/Next move
 *   between them.
 * - A **pane** is one screen of content with its own form and its own save.
 *
 * **Every step has one or more panes.** Only "Import data" has several, shown
 * as side tabs. `STEP_BY_PANE` maps a pane to its step, and `PANE_ORDER` lists
 * them all in commit order.
 */

export enum ManageIntegrationDataStep {
  SelectClients = 'SelectClients',
  LinkTrusts = 'LinkTrusts',
  LinkHoldings = 'LinkHoldings',
  ImportData = 'ImportData',
}

export const MANAGE_INTEGRATION_DATA_STEP_ORDER: ManageIntegrationDataStep[] = [
  ManageIntegrationDataStep.SelectClients,
  ManageIntegrationDataStep.LinkTrusts,
  ManageIntegrationDataStep.LinkHoldings,
  ManageIntegrationDataStep.ImportData,
];

export const MANAGE_INTEGRATION_DATA_STEP_LABELS: Record<
  ManageIntegrationDataStep,
  string
> = {
  [ManageIntegrationDataStep.SelectClients]: 'Select clients',
  [ManageIntegrationDataStep.LinkTrusts]: 'Link entities',
  [ManageIntegrationDataStep.LinkHoldings]: 'Link holdings',
  [ManageIntegrationDataStep.ImportData]: 'Import data',
};

export enum ManageIntegrationDataPane {
  SelectClients = 'SelectClients',
  LinkTrusts = 'LinkTrusts',
  LinkHoldings = 'LinkHoldings',
  ImportDataEntities = 'ImportDataEntities',
  ImportDataLiabilities = 'ImportDataLiabilities',
  ImportDataLifeInsurance = 'ImportDataLifeInsurance',
  ImportDataCashflows = 'ImportDataCashflows',
}

export type ImportDataPane =
  | ManageIntegrationDataPane.ImportDataEntities
  | ManageIntegrationDataPane.ImportDataLiabilities
  | ManageIntegrationDataPane.ImportDataLifeInsurance
  | ManageIntegrationDataPane.ImportDataCashflows;

// Linking before importing, so an entity the user linked wins over the same
// entity being imported as new.
export const PANE_ORDER: ManageIntegrationDataPane[] = [
  ManageIntegrationDataPane.SelectClients,
  ManageIntegrationDataPane.LinkTrusts,
  ManageIntegrationDataPane.LinkHoldings,
  ManageIntegrationDataPane.ImportDataEntities,
  ManageIntegrationDataPane.ImportDataLiabilities,
  ManageIntegrationDataPane.ImportDataLifeInsurance,
  ManageIntegrationDataPane.ImportDataCashflows,
];

export const STEP_BY_PANE: Record<
  ManageIntegrationDataPane,
  ManageIntegrationDataStep
> = {
  [ManageIntegrationDataPane.SelectClients]:
    ManageIntegrationDataStep.SelectClients,
  [ManageIntegrationDataPane.LinkTrusts]: ManageIntegrationDataStep.LinkTrusts,
  [ManageIntegrationDataPane.LinkHoldings]:
    ManageIntegrationDataStep.LinkHoldings,
  [ManageIntegrationDataPane.ImportDataEntities]:
    ManageIntegrationDataStep.ImportData,
  [ManageIntegrationDataPane.ImportDataLiabilities]:
    ManageIntegrationDataStep.ImportData,
  [ManageIntegrationDataPane.ImportDataLifeInsurance]:
    ManageIntegrationDataStep.ImportData,
  [ManageIntegrationDataPane.ImportDataCashflows]:
    ManageIntegrationDataStep.ImportData,
};

const ALL_PANES = Object.values(ManageIntegrationDataPane);

export function parseManageIntegrationDataPane(
  section: string | null
): ManageIntegrationDataPane | null {
  // Keep bookmarks from the old link-data wizard on the corresponding pane.
  switch (section) {
    case 'SELECT_CLIENTS':
      return ManageIntegrationDataPane.SelectClients;
    case 'DIRECT_HOLDINGS':
      return ManageIntegrationDataPane.LinkHoldings;
    case 'TRUSTS_BUSINESSES':
      return ManageIntegrationDataPane.ImportDataEntities;
    case 'LIABILITIES':
      return ManageIntegrationDataPane.ImportDataLiabilities;
    case 'LIFE_INSURANCE':
      return ManageIntegrationDataPane.ImportDataLifeInsurance;
  }
  return ALL_PANES.find((candidate) => candidate === section) ?? null;
}

export function isImportDataPane(
  pane: ManageIntegrationDataPane
): pane is ImportDataPane {
  return STEP_BY_PANE[pane] === ManageIntegrationDataStep.ImportData;
}

export interface PaneForm {
  // Resolves without writing when there's nothing to send. Returning false
  // stops for expected validation; rejecting stops for an unexpected failure.
  save: () => Promise<boolean | void>;
}

export function getStepStatus(
  stepIndex: number,
  activeIndex: number
): StepTrackerStatus {
  if (stepIndex < activeIndex) return StepTrackerStatus.Complete;
  if (stepIndex === activeIndex) return StepTrackerStatus.Active;
  return StepTrackerStatus.Inactive;
}
